/**
 * Created by lucac 
 */

import { Injectable, OnDestroy } from "@angular/core";
import { UntilDestroy } from "@ngneat/until-destroy";
import { LogCat, LogManager } from "common";
import * as _ from "lodash";
import { Observable } from "rxjs";
import { map } from 'rxjs/operators';
import { IProtocolTableXSpsCodeKey } from "../interfaces/protocol.table";
import { ProtocolTableProxy } from "./protocol.table.proxy";
import { MetadataService } from "app.services/clinical/metadata/services/metadata.service";
import { Patient } from "app.services/clinical/patient/models/patient";

@UntilDestroy()
@Injectable({ providedIn: 'root' })
  export class ProtocolTableService implements OnDestroy {

	public protocolTableXSpsCodeKey: IProtocolTableXSpsCodeKey[];

	constructor(private protocolTableProxy: ProtocolTableProxy,
		private metadata: MetadataService) {
	}

	public ngOnDestroy(): void {
	}

	public getProtocolTableXSpsCodeKey(): Observable<IProtocolTableXSpsCodeKey[]> {
		//transofrm protocoltable format to web SITE_PROT... to siteProt...
		LogManager.debug(LogCat.ProtocolTable.ProtocolTableXSpsCodeKey, `ProtocolTableService getProtocolTableXSpsCodeKey called`);
		return this.protocolTableProxy.getProtocolTableXSpsCodeKey()
			.pipe(map((x: IProtocolTableXSpsCodeKey[]) => {
				x.forEach(prot => prot.protocolTable = _.camelCase(prot.protocolTable));
				this.protocolTableXSpsCodeKey = x;
				return x;
			}));
	}

	public addMissingProtocolTables(model: Patient) {
		//Only on sps pps
		const listSiteProtPPS = this.metadata.pps ? Object.keys(this.metadata.pps).filter(key => key.startsWith('siteProt')) : [];
		const listSiteProtSPS = this.metadata.sps ? Object.keys(this.metadata.sps).filter(key => key.startsWith('siteProt')) : [];
		model.visit?.forEach(visit => {
			visit.orders?.forEach(order => {
				order.requestedProcedure?.forEach(rp => {
					rp.sps?.forEach(sps => {
						listSiteProtSPS.forEach(siteProt => sps[siteProt] ??= []);
						sps.spsXPps?.forEach(spsXPps => {
							spsXPps.pps?.forEach(pps => {
								listSiteProtPPS.forEach(siteProt => pps[siteProt] ??= []);
							});
						});
					});
				});
			});
		});
	}

	/**
	* remove from the model the protocol tables that are not mapped to sps code key,
	* to avoid sending them to the server an generating errors
	**/
	public clearNotRelevantProtocolTables(model: Patient) {
		model.visit?.forEach(visit => {
			visit.orders?.forEach(order => {
				order.requestedProcedure?.forEach(rp => {
					rp.sps?.forEach(sps => {
						let spsCodeKey = sps.spsCodeKey ?? null;
						for (const key in sps) {
							if (key.startsWith("siteProt") && sps[key]?.length > 0) {
								// Replace with an empty array if check returns false
								this.isProtocolTableMappedToSpsCodeKey(key, spsCodeKey) ? null : sps[key] = [];
							}
						}
						sps.spsXPps?.forEach(spsXPps => {
							spsXPps.pps?.forEach(pps => {
								let spsCodeKey = pps.ppsCodeKey ?? null;
								for (const key in pps) {
									if (key.startsWith("siteProt") && pps[key]?.length > 0) {
										// Replace with an empty array if check returns false
										this.isProtocolTableMappedToSpsCodeKey(key, spsCodeKey) ? null : pps[key] = [];
									}
								}
							});
						});
					});
				});
			});
		});
	}

	public isProtocolTableMappedToSpsCodeKey(protocolTable: string, spsCodeKey: number): boolean {
		if (!this.protocolTableXSpsCodeKey || !this.protocolTableXSpsCodeKey?.length) {
			LogManager.error(LogCat.ProtocolTable.General, `ProtocolTableXSpsCodeKey not loaded on shell init`);
			return false;
		} else if (!protocolTable || !spsCodeKey) {
			LogManager.debug(LogCat.ProtocolTable.General, `Missing protocolTable or spsCodeKey`);
			return false;
		} else {
			const protocolTableMapping = this.protocolTableXSpsCodeKey
				.find(x => x.protocolTable === protocolTable);
			if (!protocolTableMapping) {
				LogManager.debug(LogCat.ProtocolTable.General, `Not found protocolTable ${protocolTable} in ProtocolTableXSpsCodeKey`);
				return false;
			} else if (protocolTableMapping?.spsCodeKeys?.length === 0) {
				LogManager.debug(LogCat.ProtocolTable.General, `Not found spsCodeKeys in ProtocolTableXSpsCodeKey for protocolTable ${protocolTable}`);
				return false;
			} else {
				return protocolTableMapping.spsCodeKeys.includes(spsCodeKey);
			}
		}
	}
}
